import React, { createContext, useContext, useState, useEffect, PropsWithChildren, useCallback, useRef, useMemo } from 'react';
import { Configuration, BillingApi, BillingSubscriptionDto } from '../api';
import apiAxios from '../authentication/apiClient';
import { useUser } from './UserContext';

const apiConfig = new Configuration({ basePath: process.env.REACT_APP_API_URL || 'http://localhost:8080' });
const billingApi = new BillingApi(apiConfig, undefined, apiAxios);

interface BillingContextType {
    subscription: BillingSubscriptionDto | null;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    isTrialActive: boolean;
    isPaid: boolean;
    isReadOnly: boolean;
    trialDaysRemaining: number | null;
    trialWordsRemaining: number;
    trialPercentUsed: number;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export const BillingProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const user = useUser();
    const [subscription, setSubscription] = useState<BillingSubscriptionDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hasFetchedRef = useRef(false);
    const isFetchingRef = useRef(false);

    const fetchBilling = useCallback(async (force = false) => {
        if (!user.email) {
            setLoading(false);
            hasFetchedRef.current = true;
            return;
        }

        if (hasFetchedRef.current && !force) {
            setLoading(false);
            return;
        }

        if (isFetchingRef.current) {
            return;
        }

        try {
            isFetchingRef.current = true;
            setLoading(true);
            setError(null);
            const response = await billingApi.getSubscription();
            setSubscription(response.data);
            hasFetchedRef.current = true;
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to load billing');
            setSubscription(null);
            hasFetchedRef.current = true;
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [user.email]);

    const refresh = useCallback(async () => {
        await fetchBilling(true);
    }, [fetchBilling]);

    useEffect(() => {
        fetchBilling();
    }, [fetchBilling]);

    useEffect(() => {
        hasFetchedRef.current = false;
        isFetchingRef.current = false;
        setSubscription(null);
        setError(null);
    }, [user.email]);

    const isTrialActive = subscription?.onTrial === true && subscription?.trialStatus === 'ACTIVE';
    const isPaid = subscription?.hasActiveSubscription === true && !subscription?.onTrial;
    const isReadOnly = subscription?.readOnly === true;

    const trialDaysRemaining = useMemo(() => {
        if (!subscription?.trialEndsAt) return null;
        const diff = new Date(subscription.trialEndsAt).getTime() - Date.now();
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    }, [subscription?.trialEndsAt]);

    const trialWordsRemaining = subscription?.trialRemainingWords ?? 0;

    const trialPercentUsed = useMemo(() => {
        const used = subscription?.trialUsedWords ?? 0;
        return Math.min(100, Math.round((used / 5_000) * 100));
    }, [subscription?.trialUsedWords]);

    const contextValue: BillingContextType = {
        subscription,
        loading,
        error,
        refresh,
        isTrialActive,
        isPaid,
        isReadOnly,
        trialDaysRemaining,
        trialWordsRemaining,
        trialPercentUsed,
    };

    return (
        <BillingContext.Provider value={contextValue}>
            {children}
        </BillingContext.Provider>
    );
};

export const useBilling = (): BillingContextType => {
    const context = useContext(BillingContext);
    if (!context) {
        throw new Error('useBilling must be used within a BillingProvider');
    }
    return context;
};
